import { Fragment, useState, useEffect, useContext } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  RequireAuth,
  useIsAuthenticated,
  useSignIn,
  useSignOut,
} from "react-auth-kit";

import Flows from "./pages/Flows/FLows";
import Users from "./pages/Management/Users";
import FlowsAI from "./pages/NotificationsAI/FlowsAI";
import NotificationVerification from "./pages/NotificationsAI/NotificationVerification";
import ChangePassword from "./pages/Account/ChangePassword";
import Login from "./pages/Account/Login";
import GeneralErrors from "./pages/Errors/GeneralErrors";
import FlowNotificationsAI from "./pages/NotificationsAI/FlowNotificationsAI";
import ConfirmEmail from "./pages/Account/ConfirmEmail";
import MunicipalityChangeModal from "./components/Modals/MunicipalityChangeModal";
import Navigation from "./components/Header/Navigation";
import ClientConfiguration from "./pages/Management/ClientConfiguration";
import ExternalFlowConfiguration from "./pages/Management/ExternalFlowConfiguration";
import NotificationEdit from "./pages/NotificationsAI/NotificationEdit";
import ClientDocumentsConfiguration from "./pages/Management/ClientDocumentsConfiguration";
import ClientDocumentPhrasesConfiguration from "./pages/Management/ClientDocumentPhrasesConfiguration";
import Preview from "./pages/Management/Preview";
import UserContext from "./store/user-context";
import DocumentCheck from "./pages/Management/DocumentCheck";
import LoginMFA from "./pages/Account/LoginMFA";
import ExternalFlows from "./pages/ExternalFlows/ExternalFlows";
import UploadFiles from "./pages/NotificationsAI/UploadFiles";
import AuthModuleConfiguration from "./pages/Authorization/AuthModuleConfiguration";
import AuthModuleFlows from "./pages/Authorization/AuthModuleFlows";
import AuthModuleInvalidFlows from "./pages/Authorization/AuthModuleInvalidFlows";
import AuthModuleFlowNotifications from "./pages/Authorization/AuthModuleFlowNotifications";
import AuthModuleFlowEconNotifications from "./pages/Authorization/AuthModuleFlowEconNotifications";
import AuthModuleFlowsWithFailedCalculations from "./pages/Authorization/AuthModuleFlowsWithFailedCalculations";
import AuthModuleFlowsEcon from "./pages/Authorization/AuthModuleFlowsEcon";
import AuthModuleArchive from "./pages/Authorization/AuthModuleArchive";
import NotificationF24Edit from "./pages/NotificationsAI/NotificationF24Edit";
import NotificationVerificationf24 from "./pages/NotificationsAI/NotificationVerificationf24";

import secureLocalStorage from "react-secure-storage";

import { Alert } from "design-react-kit";
import "./App.css";
import "bootstrap-italia/dist/css/bootstrap-italia.min.css";
import "@fontsource/titillium-web";
import {
  getTokenFromCookie,
  sendApiRequest,
} from "./services/Base/base-service";
import {
  clearSecureStorage,
  clearUserContext,
  getLocalStorageByPattern,
} from "./services/helpers";
import StatisticsSummary from "./pages/Statistics/StatisticsSummary";
import StatisticsDetails from "./pages/Statistics/StatisticsDetails";

function App() {
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userRoles, setUserRoles] = useState(null);
  const [selectedClientFiscalCode, setSelectedClientFiscalCode] =
    useState(null);
  const [selectedClientName, setSelectedClientName] = useState(null);
  const [selectedClientTwoFactorEnabled, setSelectedClientTwoFactorEnabled] =
    useState(null);
  const [twoFactorAuthResendToken, setTwoFactorAuthResendToken] =
    useState(null);
  const [isAIModuleAllowed, setIsAIModuleAllowed] = useState(false);
  const [isEfModuleAllowed, setIsEfModuleAllowed] = useState(false);
  const [isAuthModuleAllowed, setIsAuthModuleAllowed] = useState(false);
  const [isStatisticModuleAllowed, setIsStatisticModuleAllowed] =
    useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const isAuthenticated = useIsAuthenticated();

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const signOutHanler = () => {
    signOut();
    clearSecureStorage();
    clearUserContext(userContext);
    navigate("/");
  };

  useEffect(() => {
    if (isAuthenticated()) {
      let userIdFromStorage = null;
      let emailFromStorage = null;
      let isAdminFromStorage = null;
      let userRolesFromStorage = null;
      let lastClientFromStorage = null;
      let isAIModuleAllowedFromStorage = null;
      let isEfModuleAllowedFromStorage = null;
      let isAuthModuleAllowedFromStorage = null;
      let isStatisticModuleAllowedFromStorage = null;

      if (secureLocalStorage.getItem("userId") !== null) {
        userIdFromStorage = secureLocalStorage.getItem("userId");
      }

      if (secureLocalStorage.getItem("email") !== null) {
        emailFromStorage = secureLocalStorage.getItem("email");
      }

      if (secureLocalStorage.getItem("isAdmin") !== null) {
        isAdminFromStorage = secureLocalStorage.getItem("isAdmin");
      }

      if (secureLocalStorage.getItem("userRoles") !== null) {
        userRolesFromStorage = secureLocalStorage.getItem("userRoles");
      }

      if (secureLocalStorage.getItem("selectedClient") !== null) {
        lastClientFromStorage = secureLocalStorage.getItem("selectedClient");
      }

      if (secureLocalStorage.getItem("isAIModuleAllowed") !== null) {
        isAIModuleAllowedFromStorage =
          secureLocalStorage.getItem("isAIModuleAllowed");
      }

      if (secureLocalStorage.getItem("isEfModuleAllowed") !== null) {
        isEfModuleAllowedFromStorage =
          secureLocalStorage.getItem("isEfModuleAllowed");
      }

      if (secureLocalStorage.getItem("isAuthModuleAllowed") !== null) {
        isAuthModuleAllowedFromStorage = secureLocalStorage.getItem(
          "isAuthModuleAllowed",
        );
      }

      if (secureLocalStorage.getItem("isStatisticModuleAllowed") !== null) {
        isStatisticModuleAllowedFromStorage = secureLocalStorage.getItem(
          "isStatisticModuleAllowed",
        );
      }

      const rawUserId = getLocalStorageByPattern("userId");
      const rawEmail = getLocalStorageByPattern("email");
      const rawUserRoles = getLocalStorageByPattern("userRoles");
      const rawSelectedClient = getLocalStorageByPattern("selectedClient");

      if (rawUserId !== null && userIdFromStorage === null) {
        signOutHanler();
      }

      if (rawEmail !== null && emailFromStorage === null) {
        signOutHanler();
      }

      if (rawUserRoles !== null && userRolesFromStorage === null) {
        signOutHanler();
      }

      if (rawSelectedClient !== null && lastClientFromStorage === null) {
        signOutHanler();
      }

      if (
        isAuthenticated() &&
        isAdminFromStorage &&
        lastClientFromStorage === null
      ) {
        setModalOpen(true);
        setUserId(userIdFromStorage);
        setUserEmail(emailFromStorage);
        setIsAdmin(isAdminFromStorage);
        setUserRoles(userRolesFromStorage);
        setSelectedClientFiscalCode(null);
        setSelectedClientName(null);
        setIsAIModuleAllowed(isAIModuleAllowedFromStorage);
        setIsEfModuleAllowed(isEfModuleAllowedFromStorage);
        setIsAuthModuleAllowed(isAuthModuleAllowedFromStorage);
        setIsStatisticModuleAllowed(isStatisticModuleAllowedFromStorage);
      } else {
        setModalOpen(false);
        setUserId(userIdFromStorage);
        setUserEmail(emailFromStorage);
        setIsAdmin(isAdminFromStorage);
        setUserRoles(userRolesFromStorage);
        setSelectedClientFiscalCode(
          lastClientFromStorage !== null
            ? lastClientFromStorage.fiscalCode
            : null,
        );
        setSelectedClientName(
          lastClientFromStorage !== null ? lastClientFromStorage.name : null,
        );
        setSelectedClientTwoFactorEnabled(
          lastClientFromStorage !== null
            ? lastClientFromStorage.twoFactorEnabled
            : null,
        );
        setIsAIModuleAllowed(isAIModuleAllowedFromStorage);
        setIsEfModuleAllowed(isEfModuleAllowedFromStorage);
        setIsAuthModuleAllowed(isAuthModuleAllowedFromStorage);
        setIsStatisticModuleAllowed(isStatisticModuleAllowedFromStorage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    userEmail,
    isAdmin,
    selectedClientFiscalCode,
    selectedClientName,
    isAIModuleAllowed,
    isEfModuleAllowed,
    isAuthModuleAllowed,
    isStatisticModuleAllowed,
    isAuthenticated,
  ]);

  const modalCloseHandler = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    if (isAuthenticated() && userId !== null) {
      setTimeout(() => {
        if (getTokenFromCookie() !== "") {
          getUserRoles(userId);
        }
      }, 100);
    }
    const interval = setInterval(() => {
      if (userId !== null) {
        getUserRoles(userId);
      }
    }, process.env.REACT_APP_RENEW_USER_ROLES_TIME);

    return () => {
      clearInterval(interval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const getUserRoles = (userId) => {
    sendApiRequest(
      `User/GetUserRoles?userId=${userId}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext,
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          if (response.data !== undefined && response.data !== null) {
            secureLocalStorage.setItem("userRoles", response.data);
          }
        }
      }
    });
  };

  return (
    <Fragment>
      <UserContext.Provider
        value={{
          userId,
          userEmail,
          userRoles,
          isAdmin,
          selectedClientFiscalCode,
          selectedClientName,
          selectedClientTwoFactorEnabled,
          twoFactorAuthResendToken,
          isAIModuleAllowed,
          isEfModuleAllowed,
          isAuthModuleAllowed,
          isStatisticModuleAllowed,
          setSelectedClientFiscalCode,
          setSelectedClientName,
          setSelectedClientTwoFactorEnabled,
          setUserRoles,
          setIsAdmin,
          setUserEmail,
          setUserId,
          setTwoFactorAuthResendToken,
          setIsEfModuleAllowed,
          setIsAIModuleAllowed,
          setIsAuthModuleAllowed,
          setIsStatisticModuleAllowed,
        }}
      >
        <MunicipalityChangeModal
          modalOpen={modalOpen}
          modalCloseHandler={modalCloseHandler}
        />
        {error !== null && error.length !== undefined && (
          <Alert color="danger" className="mx-auto w-100 mb-5">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}
        {isAuthenticated() && <Navigation />}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login-mfa" element={<LoginMFA />} />
          <Route path="/confirmEmail" element={<ConfirmEmail />} />
          <Route path="/changePassword" element={<ChangePassword />} />
          <Route path="/changePasswordSelf" element={<ChangePassword />} />
          <Route
            path="/"
            element={
              <RequireAuth loginPath={"/login"}>
                <Flows />
              </RequireAuth>
            }
          />
          <Route
            path="/externalFlows"
            element={
              <RequireAuth loginPath={"/login"}>
                <ExternalFlows />
              </RequireAuth>
            }
          />
          {userRoles !== null && (userRoles.includes("PAAdmin") || isAdmin) && (
            <Route
              path="/users"
              element={
                <RequireAuth loginPath={"/login"}>
                  <Users />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/clientConfiguration"
              element={
                <RequireAuth loginPath={"/login"}>
                  <ClientConfiguration />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/clientConfiguration/clientDocumentsConfiguration/:client/:clientId"
              element={
                <RequireAuth loginPath={"/login"}>
                  <ClientDocumentsConfiguration />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/clientConfiguration/clientDocumentsConfiguration/clientDocumentPhrasesConfiguration/:client/:clientId/:document/:documentTypeId/:clientDocumentRuleId"
              element={
                <RequireAuth loginPath={"/login"}>
                  <ClientDocumentPhrasesConfiguration />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/clientConfiguration/clientDocumentsConfiguration/preview/:clientId/:documentTypeId"
              element={
                <RequireAuth loginPath={"/login"}>
                  <Preview />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/clientConfiguration/check/:clientId/:client"
              element={
                <RequireAuth loginPath={"/login"}>
                  <DocumentCheck />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/externalFlowConfiguration"
              element={
                <RequireAuth loginPath={"/login"}>
                  <ExternalFlowConfiguration />
                </RequireAuth>
              }
            />
          )}
          {isAdmin && (
            <Route
              path="/authModuleConfiguration"
              element={
                <RequireAuth loginPath={"/login"}>
                  <AuthModuleConfiguration />
                </RequireAuth>
              }
            />
          )}
          <Route path="/errors" element={<GeneralErrors />} />
          <Route
            path="/notificheAI/flows"
            element={
              <RequireAuth loginPath={"/login"}>
                <FlowsAI />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/flows/notifications/:flowId"
            element={
              <RequireAuth loginPath={"/login"}>
                <FlowNotificationsAI />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/verification"
            element={
              <RequireAuth loginPath={"/login"}>
                <NotificationVerification />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/verificationf24"
            element={
              <RequireAuth loginPath={"/login"}>
                <NotificationVerificationf24 />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/uploadFiles"
            element={
              <RequireAuth loginPath={"/login"}>
                <UploadFiles />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/verification/edit/:id/"
            element={
              <RequireAuth loginPath={"/login"}>
                <NotificationEdit />
              </RequireAuth>
            }
          />
          <Route
            path="/notificheAI/verificationf24/edit/:id/:clientId"
            element={
              <RequireAuth loginPath={"/login"}>
                <NotificationF24Edit />
              </RequireAuth>
            }
          />
          <Route
            path="/statistics"
            element={
              <RequireAuth loginPath={"/login"}>
                <StatisticsSummary />
              </RequireAuth>
            }
          />
          <Route
            path="/statistics/details"
            element={
              <RequireAuth loginPath={"/login"}>
                <StatisticsDetails />
              </RequireAuth>
            }
          />
          {userRoles !== null &&
            (userRoles.includes("PASectorManager") || isAdmin) && (
              <Route
                path="/authModule/technicalFlows"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleFlows />
                  </RequireAuth>
                }
              />
            )}
          {userRoles !== null &&
            (userRoles.includes("PAEconomicalManager") || isAdmin) && (
              <Route
                path="/authModule/economicalFlows"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleFlowsEcon />
                  </RequireAuth>
                }
              />
            )}
          {userRoles !== null &&
            (userRoles.includes("PASectorManager") || isAdmin) && (
              <Route
                path="/authModule/technicalFlows/notifications/:client/:flowId/:flowType"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleFlowNotifications />
                  </RequireAuth>
                }
              />
            )}
          {userRoles !== null &&
            (userRoles.includes("PAEconomicalManager") || isAdmin) && (
              <Route
                path="/authModule/economicalFlows/notifications/:client/:flowId/:flowType"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleFlowEconNotifications />
                  </RequireAuth>
                }
              />
            )}
          {userRoles !== null &&
            (userRoles.includes("PASectorManager") ||
              userRoles.includes("PAEconomicalManager") ||
              isAdmin) && (
              <Route
                path="/authModule/invalidFlows"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleInvalidFlows />
                  </RequireAuth>
                }
              />
            )}
          {isAdmin && (
            <Route
              path="/authModule/flowsWithFailedCostCalculation"
              element={
                <RequireAuth loginPath={"/login"}>
                  <AuthModuleFlowsWithFailedCalculations />
                </RequireAuth>
              }
            />
          )}
          {userRoles !== null &&
            (userRoles.includes("PASectorManager") ||
              userRoles.includes("PAEconomicalManager") ||
              isAdmin) && (
              <Route
                path="/authModule/flowsArchive"
                element={
                  <RequireAuth loginPath={"/login"}>
                    <AuthModuleArchive />
                  </RequireAuth>
                }
              />
            )}
          <Route path="*" element={<GeneralErrors />} />
        </Routes>
      </UserContext.Provider>
    </Fragment>
  );
}

export default App;
